(() => {
  'use strict';

  /* =========================================================
     SETLARO SIZE GUIDE DRAWER
     ---------------------------------------------------------
     功能：
     1. Drawer 打开时移到 <body>，避免受到 Product information
        父级 transform / animation / overflow 的影响
     2. PC 右侧 Drawer
     3. Mobile 全屏 Drawer
     4. TOP / BOTTOM tabs
     5. IN / CM conversion
     6. Overlay / ESC / Close button 关闭
     7. Background scroll lock
     8. Focus trap
     9. Close 后恢复页面位置和按钮焦点
  ========================================================== */


  /* ---------------------------------------------------------
     防止脚本被重复初始化
  --------------------------------------------------------- */

  if (window.SetlaroSizeGuide) {
    return;
  }


  const state = {
    activeLayer: null,
    activeDrawer: null,
    trigger: null,
    scrollY: 0
  };


  /* =========================================================
     HELPERS
  ========================================================== */


  /**
   * 找到当前按钮对应的 Size Guide Layer
   */
  function getLayerFromTrigger(trigger) {
    const id = trigger.getAttribute('data-size-guide-open');

    if (!id) {
      return null;
    }

    return document.querySelector(
      `[data-size-guide-layer="${CSS.escape(id)}"]`
    );
  }


  /**
   * CM → IN
   *
   * 支持：
   * 88
   * 88.5
   * 88-96
   * 88–96
   */
  function cmToInches(value) {
    if (!value) {
      return value;
    }

    return String(value).replace(
      /\d+(?:\.\d+)?/g,
      number => {
        const inches = parseFloat(number) / 2.54;

        return inches
          .toFixed(1)
          .replace(/\.0$/, '');
      }
    );
  }


  /**
   * 标准化 CM 范围里的连字符
   */
  function formatCm(value) {
    if (!value) {
      return value;
    }

    return String(value).replace(/-/g, '–');
  }



  /* =========================================================
     UNIT SWITCHER
  ========================================================== */

  function setUnit(layer, unit) {
    if (!layer) {
      return;
    }


    /* 更新 IN / CM 按钮 */

    const buttons = layer.querySelectorAll(
      '[data-size-unit]'
    );

    buttons.forEach(button => {
      const isActive =
        button.dataset.sizeUnit === unit;

      button.classList.toggle(
        'is-active',
        isActive
      );

      button.setAttribute(
        'aria-pressed',
        isActive ? 'true' : 'false'
      );
    });


    /* 更新表格数字 */

    const cells = layer.querySelectorAll(
      '[data-cm]'
    );

    cells.forEach(cell => {
      const cmValue = cell.dataset.cm;

      const output = cell.querySelector(
        '[data-size-value]'
      );

      if (!output) {
        return;
      }

      output.textContent =
        unit === 'in'
          ? cmToInches(cmValue)
          : formatCm(cmValue);
    });


    /* 保存当前单位 */

    layer.dataset.currentUnit = unit;
  }



  /* =========================================================
     TOP / BOTTOM TABS
  ========================================================== */

  function setTab(layer, tabName) {
    if (!layer) {
      return;
    }


    const tabs = layer.querySelectorAll(
      '[data-size-tab]'
    );

    const panels = layer.querySelectorAll(
      '[data-size-panel]'
    );


    tabs.forEach(tab => {
      const isActive =
        tab.dataset.sizeTab === tabName;

      tab.classList.toggle(
        'is-active',
        isActive
      );

      tab.setAttribute(
        'aria-selected',
        isActive ? 'true' : 'false'
      );

      tab.setAttribute(
        'tabindex',
        isActive ? '0' : '-1'
      );
    });


    panels.forEach(panel => {
      const isActive =
        panel.dataset.sizePanel === tabName;

      panel.hidden = !isActive;

      panel.setAttribute(
        'aria-hidden',
        isActive ? 'false' : 'true'
      );
    });


    layer.dataset.currentTab = tabName;
  }



  /* =========================================================
     MOVE DRAWER TO BODY
  ========================================================== */

  /**
   * 非常关键。
   *
   * 你的 Size Guide Block 本来位于：
   *
   * Product information
   * └── main-product__block
   *     └── Size Guide
   *
   * Minimog 的父层可能带 transform / animation / overflow。
   *
   * 因此 position: fixed 仍可能被父级限制。
   *
   * 打开时把 Layer 临时移动到：
   *
   * body
   * └── Size Guide Layer
   *
   * 这样它才能真正覆盖浏览器窗口。
   */
  function portalToBody(layer) {
    if (!layer) {
      return;
    }


    /* 第一次打开时记录原始位置 */

    if (!layer.__setlaroOriginalParent) {
      layer.__setlaroOriginalParent =
        layer.parentNode;

      layer.__setlaroOriginalNextSibling =
        layer.nextSibling;
    }


    /* 如果之前还有恢复计时器，取消 */

    if (layer.__setlaroRestoreTimer) {
      clearTimeout(
        layer.__setlaroRestoreTimer
      );

      layer.__setlaroRestoreTimer = null;
    }


    /* 移到 body 最外层 */

    if (layer.parentNode !== document.body) {
      document.body.appendChild(layer);
    }
  }



  /**
   * Drawer 关闭动画结束以后，
   * 再放回 Product block。
   *
   * 这样 Shopify Theme Editor 仍然能够正常管理 block。
   */
  function restorePortal(layer) {
    if (!layer) {
      return;
    }


    layer.__setlaroRestoreTimer =
      window.setTimeout(() => {

        const parent =
          layer.__setlaroOriginalParent;

        const nextSibling =
          layer.__setlaroOriginalNextSibling;


        /*
         * Shopify Theme Editor 有时会重新渲染 Section。
         * 如果原父元素已经不存在，就不要强行恢复。
         */

        if (
          !parent ||
          !document.documentElement.contains(parent)
        ) {
          layer.__setlaroRestoreTimer = null;
          return;
        }


        if (
          nextSibling &&
          nextSibling.parentNode === parent
        ) {
          parent.insertBefore(
            layer,
            nextSibling
          );
        } else {
          parent.appendChild(layer);
        }


        layer.__setlaroRestoreTimer = null;

      }, 360);
  }



  /* =========================================================
     BACKGROUND SCROLL LOCK
  ========================================================== */

  function lockPageScroll() {
    state.scrollY =
      window.scrollY ||
      window.pageYOffset ||
      0;


    /*
     * 记录当前 scrollbar 宽度。
     * PC 打开 Drawer 时避免页面突然横向跳动。
     */

    const scrollbarWidth =
      window.innerWidth -
      document.documentElement.clientWidth;


    document.documentElement.classList.add(
      'setlaro-size-guide-open'
    );

    document.body.classList.add(
      'setlaro-size-guide-open'
    );


    document.body.style.setProperty(
      '--setlaro-scrollbar-width',
      `${Math.max(scrollbarWidth, 0)}px`
    );
  }


  function unlockPageScroll() {
    document.documentElement.classList.remove(
      'setlaro-size-guide-open'
    );

    document.body.classList.remove(
      'setlaro-size-guide-open'
    );

    document.body.style.removeProperty(
      '--setlaro-scrollbar-width'
    );
  }



  /* =========================================================
     FOCUS
  ========================================================== */

  function getFocusableElements(drawer) {
    if (!drawer) {
      return [];
    }


    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'summary',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');


    return Array
      .from(
        drawer.querySelectorAll(selector)
      )
      .filter(element => {

        if (element.hidden) {
          return false;
        }

        const style =
          window.getComputedStyle(element);

        if (
          style.display === 'none' ||
          style.visibility === 'hidden'
        ) {
          return false;
        }

        return (
          element.offsetWidth > 0 ||
          element.offsetHeight > 0
        );
      });
  }



  /* =========================================================
     OPEN DRAWER
  ========================================================== */

  function openDrawer(trigger) {
    const layer =
      getLayerFromTrigger(trigger);

    if (!layer) {
      console.warn(
        '[SETLARO Size Guide] Drawer layer not found.'
      );

      return;
    }


    /*
     * 如果已有另一个 Drawer 开着，
     * 先关闭。
     */

    if (
      state.activeLayer &&
      state.activeLayer !== layer
    ) {
      closeDrawer(false);
    }


    portalToBody(layer);


    const drawer = layer.querySelector(
      '.setlaro-size-guide-drawer'
    );

    if (!drawer) {
      return;
    }


    state.activeLayer = layer;
    state.activeDrawer = drawer;
    state.trigger = trigger;


    /* -----------------------------------------
       每次打开默认回到 TOP
    ------------------------------------------ */

    setTab(
      layer,
      'top'
    );


    /* -----------------------------------------
       默认单位
    ------------------------------------------ */

    const defaultUnit =
      layer.dataset.defaultUnit || 'in';

    setUnit(
      layer,
      defaultUnit
    );


    /* -----------------------------------------
       Drawer 内容回到顶部
    ------------------------------------------ */

    const drawerBody = layer.querySelector(
      '.setlaro-size-guide-body'
    );

    if (drawerBody) {
      drawerBody.scrollTop = 0;
    }


    /* -----------------------------------------
       关闭打开着的 How to Measure
    ------------------------------------------ */

    layer
      .querySelectorAll(
        '.setlaro-measure-details[open]'
      )
      .forEach(details => {
        details.removeAttribute('open');
      });


    /* -----------------------------------------
       Lock 页面
    ------------------------------------------ */

    lockPageScroll();


    /* -----------------------------------------
       显示 Layer
    ------------------------------------------ */

    layer.setAttribute(
      'aria-hidden',
      'false'
    );


    /*
     * 两个 requestAnimationFrame，
     * 确保元素已经移动到 body 并完成 layout，
     * 再触发 CSS transition。
     */

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        layer.classList.add(
          'is-open'
        );
      });
    });


    /* -----------------------------------------
       焦点进入关闭按钮
    ------------------------------------------ */

    const closeButton = layer.querySelector(
      '.setlaro-size-guide-close'
    );


    if (closeButton) {
      window.setTimeout(() => {

        try {
          closeButton.focus({
            preventScroll: true
          });
        } catch (error) {
          closeButton.focus();
        }

      }, 50);
    }


    /* -----------------------------------------
       Custom Event
    ------------------------------------------ */

    document.dispatchEvent(
      new CustomEvent(
        'setlaro:size-guide:open',
        {
          detail: {
            layer,
            drawer
          }
        }
      )
    );
  }



  /* =========================================================
     CLOSE DRAWER
  ========================================================== */

  function closeDrawer(returnFocus = true) {
    const layer =
      state.activeLayer;

    const trigger =
      state.trigger;


    if (!layer) {
      return;
    }


    /* 开始关闭动画 */

    layer.classList.remove(
      'is-open'
    );


    layer.setAttribute(
      'aria-hidden',
      'true'
    );


    unlockPageScroll();


    /* -----------------------------------------
       动画结束后恢复 DOM 位置
    ------------------------------------------ */

    restorePortal(layer);


    state.activeLayer = null;
    state.activeDrawer = null;
    state.trigger = null;


    /* -----------------------------------------
       焦点回到 Size Guide 按钮
    ------------------------------------------ */

    if (
      returnFocus &&
      trigger &&
      document.documentElement.contains(trigger)
    ) {

      window.setTimeout(() => {

        try {
          trigger.focus({
            preventScroll: true
          });
        } catch (error) {
          trigger.focus();
        }

      }, 30);
    }


    /* -----------------------------------------
       Custom Event
    ------------------------------------------ */

    document.dispatchEvent(
      new CustomEvent(
        'setlaro:size-guide:close',
        {
          detail: {
            layer
          }
        }
      )
    );
  }



  /* =========================================================
     CLICK EVENTS
  ========================================================== */

  function handleClick(event) {

    /* -----------------------------------------
       OPEN
    ------------------------------------------ */

    const openButton =
      event.target.closest(
        '[data-size-guide-open]'
      );


    if (openButton) {
      event.preventDefault();

      openDrawer(
        openButton
      );

      return;
    }


    /* -----------------------------------------
       CLOSE
       包括：
       - X
       - Overlay
    ------------------------------------------ */

    const closeButton =
      event.target.closest(
        '[data-size-guide-close]'
      );


    if (
      closeButton &&
      state.activeLayer
    ) {
      event.preventDefault();

      closeDrawer();

      return;
    }


    /* -----------------------------------------
       TOP / BOTTOM
    ------------------------------------------ */

    const tabButton =
      event.target.closest(
        '[data-size-tab]'
      );


    if (tabButton) {
      const layer =
        tabButton.closest(
          '[data-size-guide-layer]'
        );

      if (!layer) {
        return;
      }

      setTab(
        layer,
        tabButton.dataset.sizeTab
      );

      return;
    }


    /* -----------------------------------------
       IN / CM
    ------------------------------------------ */

    const unitButton =
      event.target.closest(
        '[data-size-unit]'
      );


    if (unitButton) {
      const layer =
        unitButton.closest(
          '[data-size-guide-layer]'
        );

      if (!layer) {
        return;
      }

      setUnit(
        layer,
        unitButton.dataset.sizeUnit
      );
    }
  }



  /* =========================================================
     KEYBOARD EVENTS
  ========================================================== */

  function handleKeydown(event) {
    if (
      !state.activeLayer ||
      !state.activeDrawer
    ) {
      return;
    }


    /* -----------------------------------------
       ESC CLOSE
    ------------------------------------------ */

    if (event.key === 'Escape') {
      event.preventDefault();

      closeDrawer();

      return;
    }


    /* -----------------------------------------
       Arrow keyboard navigation for tabs
    ------------------------------------------ */

    const activeTab =
      event.target.closest(
        '[data-size-tab]'
      );


    if (
      activeTab &&
      (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight'
      )
    ) {

      const tabs = Array.from(
        state.activeLayer.querySelectorAll(
          '[data-size-tab]'
        )
      );


      if (tabs.length < 2) {
        return;
      }


      event.preventDefault();


      const currentIndex =
        tabs.indexOf(activeTab);


      let nextIndex;

      if (event.key === 'ArrowRight') {
        nextIndex =
          (currentIndex + 1) %
          tabs.length;
      } else {
        nextIndex =
          (
            currentIndex -
            1 +
            tabs.length
          ) %
          tabs.length;
      }


      const nextTab =
        tabs[nextIndex];


      setTab(
        state.activeLayer,
        nextTab.dataset.sizeTab
      );


      nextTab.focus();

      return;
    }


    /* -----------------------------------------
       FOCUS TRAP
    ------------------------------------------ */

    if (event.key !== 'Tab') {
      return;
    }


    const focusable =
      getFocusableElements(
        state.activeDrawer
      );


    if (!focusable.length) {
      return;
    }


    const first =
      focusable[0];

    const last =
      focusable[
        focusable.length - 1
      ];


    if (
      event.shiftKey &&
      document.activeElement === first
    ) {

      event.preventDefault();

      last.focus();

      return;
    }


    if (
      !event.shiftKey &&
      document.activeElement === last
    ) {

      event.preventDefault();

      first.focus();
    }
  }



  /* =========================================================
     SHOPIFY THEME EDITOR SUPPORT
  ========================================================== */

  function handleShopifySectionUnload(event) {
    if (!state.activeLayer) {
      return;
    }


    const section =
      state.activeLayer
        .__setlaroOriginalParent
        ?.closest?.(
          '.shopify-section'
        );


    if (
      section &&
      section.id === event.target.id
    ) {
      closeDrawer(false);
    }
  }



  /* =========================================================
     INIT
  ========================================================== */

  document.addEventListener(
    'click',
    handleClick
  );


  document.addEventListener(
    'keydown',
    handleKeydown
  );


  document.addEventListener(
    'shopify:section:unload',
    handleShopifySectionUnload
  );


  /* -----------------------------------------
     暴露一个小 API，方便以后调试
  ------------------------------------------ */

  window.SetlaroSizeGuide = {
    open(trigger) {
      openDrawer(trigger);
    },

    close() {
      closeDrawer();
    },

    setTab(tabName) {
      if (state.activeLayer) {
        setTab(
          state.activeLayer,
          tabName
        );
      }
    },

    setUnit(unit) {
      if (state.activeLayer) {
        setUnit(
          state.activeLayer,
          unit
        );
      }
    }
  };

})();